import React, { useState } from 'react';
import { X, Upload, Loader2, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { clientsAPI } from '../../../services/api';

const MigrateToClientModal = ({ selectedCompanies, onClose, onMigrate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    hiringManager: '',
    pocName: '',
    pocJobTitle: '',
    pocEmail: '',
    pocPhone: '',
  });
  const [files, setFiles] = useState({
    logo: null,
    primaryAgreement: null,
    workOrder: null,
    supportingDocs: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fieldName) => {
    if (fieldName === 'supportingDocs') {
      setFiles(prev => ({ ...prev, supportingDocs: Array.from(e.target.files) }));
    } else {
      setFiles(prev => ({ ...prev, [fieldName]: e.target.files[0] || null }));
    }
  };

  const handleSubmit = async () => {
    if (selectedCompanies.length === 0) return;

    setLoading(true);
    const results = { success: [], failed: [], alreadyExists: [], alreadyMigrated: [] };

    for (const company of selectedCompanies) {
      try {
        const data = new FormData();
        data.append('companyId', company.id);
        data.append('role', 'CLIENT');
        if (formData.hiringManager) data.append('hiringManager', formData.hiringManager);
        if (formData.pocName)        data.append('pocName', formData.pocName);
        if (formData.pocJobTitle)    data.append('pocJobTitle', formData.pocJobTitle);
        if (formData.pocEmail)       data.append('pocEmail', formData.pocEmail);
        if (formData.pocPhone)       data.append('pocPhone', formData.pocPhone);
        if (files.logo)              data.append('logo', files.logo);
        if (files.primaryAgreement)  data.append('primaryAgreement', files.primaryAgreement);
        if (files.workOrder)         data.append('workOrder', files.workOrder);
        files.supportingDocs.forEach(doc => data.append('supportingDocs', doc));

        await clientsAPI.migrateClient(data);
        results.success.push(company.companyName);

      } catch (error) {
        const msg = error.response?.data?.message || '';
        if (msg.toLowerCase().includes('already migrated')) {
          results.alreadyMigrated.push(company.companyName);
        } else if (msg.toLowerCase().includes('already exists')) {
          results.alreadyExists.push(company.companyName);
        } else {
          results.failed.push(company.companyName);
        }
      }
    } // ← this closing brace was missing in your pasted code

    setLoading(false);

    if (results.success.length > 0)
      toast.success(`${results.success.length} company(ies) migrated successfully ✅`);
    if (results.alreadyMigrated.length > 0)
      toast.warning(`Already migrated: ${results.alreadyMigrated.join(', ')} ⚠️`);
    if (results.alreadyExists.length > 0)
      toast.warning(`Client with same name already exists: ${results.alreadyExists.join(', ')} ⚠️`);
    if (results.failed.length > 0)
      toast.error(`Failed: ${results.failed.join(', ')} ❌`);

    onMigrate?.();
    onClose();
  };

  const FileInput = ({ label, fieldName, accept, multiple }) => (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
        <Upload size={14} className="text-gray-400" />
        <span className="text-sm text-gray-500 truncate">
          {multiple
            ? files.supportingDocs.length > 0
              ? `${files.supportingDocs.length} file(s) selected`
              : 'Choose files...'
            : files[fieldName]?.name || 'Choose file...'}
        </span>
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFileChange(e, fieldName)}
          className="hidden"
        />
      </label>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Migrate to Client</h3>
            <p className="text-sm text-gray-500 mt-0.5">{selectedCompanies.length} company(ies) selected</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">

          {/* Selected company tags */}
          <div className="flex flex-wrap gap-2">
            {selectedCompanies.map(c => (
              <span key={c.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                {c.companyName}
              </span>
            ))}
          </div>

          {/* Role + Hiring Manager */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Role <span className="text-indigo-400 font-normal">(auto-assigned)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value="CLIENT"
                  readOnly
                  className="w-full px-3 py-2 pr-8 text-sm border border-gray-200 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed font-semibold"
                />
                <Lock size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Hiring Manager</label>
              <input
                type="text"
                name="hiringManager"
                value={formData.hiringManager}
                onChange={handleInputChange}
                placeholder="Hiring manager name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Point of Contact */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Point of Contact</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'pocName',     label: 'POC Name',  type: 'text',  placeholder: 'Full name' },
                { name: 'pocJobTitle', label: 'Job Title',  type: 'text',  placeholder: 'Job title' },
                { name: 'pocEmail',    label: 'Email',      type: 'email', placeholder: 'email@example.com' },
                { name: 'pocPhone',    label: 'Phone',      type: 'tel',   placeholder: '+91 XXXXX XXXXX' },
              ].map(field => (
                <div key={field.name}>
                  <label className="block text-xs text-gray-600 mb-1">{field.label}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Documents</p>
            <div className="grid grid-cols-2 gap-3">
              <FileInput label="Logo"               fieldName="logo"              accept="image/*" />
              <FileInput label="Primary Agreement"  fieldName="primaryAgreement"  accept=".pdf,.doc,.docx" />
              <FileInput label="Work Order"         fieldName="workOrder"         accept=".pdf,.doc,.docx" />
              <FileInput label="Supporting Docs"    fieldName="supportingDocs"    accept=".pdf,.doc,.docx,.xls,.xlsx" multiple />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Migrating...' : 'Migrate to Client'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default MigrateToClientModal;